import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { formatToCustomLocale } from "../../../../utilities";
import { Container, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setHoldAndWinOutroCounterFinished,
} from "../../../../State/actions";
import {
  selectCurrentRound,
  selectCurrency,
  selectIsSpinning,
  selectCounterAnimationStarted,
  selectCriticalAnimationPlaying,
  selectFreeRoundsActive,
  selectFreeRoundsTotalWin,
  selectFreeRoundsCompletionPending,
  selectFreeRoundsEnded,
  selectFreeRoundsStatus,
  selectHoldAndWinActive,
  selectHoldAndWinCurrentRound,
  selectIsFreeRoundsCompletionReload,
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
  selectSceneTransitionActive,
  selectIsHoldAndWinCompletionReload,
  selectIsHoldAndWinMidFeatureReload,
  selectIsHoldAndWinIntroReload,
  selectFeatureState,
} from "../../../../State/selectors";
import {
  selectHoldAndWinOutroCounterFinished,
  selectWinAnimationCounterFinished,
} from "../../../../State/Slices/animationsSlice";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";

/**
 * FreeRoundsWinDisplay - Win display component for free rounds
 *
 * Displays dual win amounts during free rounds:
 * - Total Win: Cumulative win across all free rounds
 * - Win: Current round win (shown temporarily)
 *
 * @param {Object} toolbar - Toolbar reference for positioning
 */
const FreeRoundsWinDisplay = ({ toolbar }) => {
  const dispatch = useDispatch();
  const currentRound = useSelector(selectCurrentRound);
  const currency = useSelector(selectCurrency);
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationStarted = useSelector(selectCounterAnimationStarted);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isFreeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeRoundsTotalWin = useSelector(selectFreeRoundsTotalWin);
  const completionPending = useSelector(selectFreeRoundsCompletionPending);
  const freeRoundsEnded = useSelector(selectFreeRoundsEnded);
  const freeRoundsStatus = useSelector(selectFreeRoundsStatus);
  const isHoldAndWinActive = useSelector(selectHoldAndWinActive);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const isFreeRoundsCompletionReload = useSelector(
    selectIsFreeRoundsCompletionReload,
  );
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const isAnimatedWin = useSelector(selectIsAnimatedWin);
  const sceneTransitionActive = useSelector(selectSceneTransitionActive);
  const isHoldAndWinCompletionReload = useSelector(selectIsHoldAndWinCompletionReload);
  const isHoldAndWinMidFeatureReload = useSelector(
    selectIsHoldAndWinMidFeatureReload,
  );
  const isHoldAndWinIntroReload = useSelector(selectIsHoldAndWinIntroReload);
  const winAnimationCounterFinished = useSelector(
    selectWinAnimationCounterFinished,
  );
  const holdAndWinOutroCounterFinished = useSelector(
    selectHoldAndWinOutroCounterFinished,
  );
  const featureState = useSelector(selectFeatureState);
  const { device, orientation, getResponsiveConfig, uiSymbolScale } =
    useDimensions();

  // True when current spin is an H&W trigger (intro phase) - same as WinDisplay
  const isHoldAndWinTriggerRound =
    featureState?.type === "holdAndWin" && featureState?.phase === "intro";

  // Latch ref: keeps win text visible after Big Win counter finishes for H&W trigger rounds.
  // The Big Win popup closes before holdAndWinActive becomes true, causing a gap where
  // showCurrentWin would be false. This bridges that gap - same pattern as WinDisplay.
  const wasHoldAndWinTriggerRoundRef = useRef(false);
  if (isHoldAndWinTriggerRound && currentRound?.win && winAnimationCounterFinished) {
    wasHoldAndWinTriggerRoundRef.current = true;
  } else if (isHoldAndWinActive || (isSpinning && !isHoldAndWinActive)) {
    wasHoldAndWinTriggerRoundRef.current = false;
  }

  // Local state to track displayed total win (updates after spin completes)
  const [displayedTotalWin, setDisplayedTotalWin] = useState(0);

  // Track if we've initialized the display (for reload scenario)
  const initializedRef = useRef(false);

  // Track if any spin has happened in this session (to detect reload vs first spin)
  const hasSpunRef = useRef(false);

  // Refs for animation
  const totalWinContainerRef = useRef(null);
  const animationRef = useRef(null);

  // Get button dimensions using the same system as PopiClock
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Memoize win configuration to avoid recalculating on every render
  const winConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let x = 0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.05;
        x = toolbar.width / 2;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.1;
        x = toolbar.width - buttonConfig.width * 8;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.7;
        x = toolbar.width / 2;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.35;
        x = toolbar.width / 1.8;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      x = toolbar.width / 1.65;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, x, y };
  }, [
    device,
    orientation,
    toolbar.width,
    toolbar.height,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  const baseFontSize = 36;
  const responsiveFontSize = baseFontSize * winConfig.fontMultiplier;
  const FONT_SIZE = Math.round(responsiveFontSize * uiSymbolScale);
  const LABEL_FONT_SIZE = Math.round(FONT_SIZE * 0.95); // Slightly smaller for label

  // Calculate line spacing for positioning
  const lineSpacing = Math.round(FONT_SIZE * 0.6);

  // Reset component state when new spin begins (NEVER during Hold and Win)
  useEffect(() => {
    // CRITICAL: This component should never be mounted during Hold and Win
    // But as a safety guard, check anyway
    if (isSpinning && !isHoldAndWinActive) {
      dispatch(setCounterAnimationStarted(false));
      dispatch(setCounterAnimationFinished(false));
      dispatch(setHoldAndWinOutroCounterFinished(false));
    }
  }, [isSpinning, isHoldAndWinActive, dispatch]);

  // Track if a spin has happened (to differentiate reload from first spin)
  useEffect(() => {
    if (isSpinning) {
      hasSpunRef.current = true;
    }
  }, [isSpinning]);

  // Initialize displayedTotalWin ONCE when component first mounts (for reload scenario)
  // CRITICAL: On reload mid-session, freeRoundsTotalWin will have accumulated wins
  // CRITICAL: On reload (both mid-session and completion), show immediately (bypass animation checks)
  // CRITICAL: On first spin, must wait for counterAnimationStarted to avoid race condition with expanding wilds
  // This prevents Total Win from appearing before Current Win on first free rounds spin
  useEffect(() => {
    // Check if we should initialize (active OR played status)
    // CRITICAL: Must check "played" status even without reload to catch first/last win scenarios
    const shouldCheckInitialization =
      isFreeRoundsActive ||
      freeRoundsStatus === "played";

    if (shouldCheckInitialization && !initializedRef.current) {
      // On H&W intro/mid-feature reload during free rounds, the API never includes the
      // trigger round win in balance.free_rounds until H&W completes. So we must always
      // add currentRound.win on top of freeRoundsTotalWin for these reload types.
      // Case 1 (no prior wins): freeRoundsTotalWin=0, result = 0 + triggerWin
      // Case 2 (prior wins exist): freeRoundsTotalWin=1140, result = 1140 + triggerWin
      // For all other reload types, use freeRoundsTotalWin as-is (server has the correct value).
      const reloadWinValue =
        (isHoldAndWinIntroReload || isHoldAndWinMidFeatureReload)
          ? (freeRoundsTotalWin > 0
              ? freeRoundsTotalWin + (currentRound?.win || 0)
              : currentRound?.win || 0)
          : freeRoundsTotalWin > 0
            ? freeRoundsTotalWin
            : 0;

      // Reload scenario: Total win exists but NO spin has happened yet in this session
      // Show immediately on reload without waiting for animations
      // CRITICAL: On H&W completion reload, show immediately even if H&W outro is active
      // CRITICAL: On H&W mid-feature reload, show immediately (Total Win should be visible during H&W)
      // On natural gameplay, wait until H&W is inactive
      const isReloadScenario =
        !hasSpunRef.current && reloadWinValue > 0 && (!isHoldAndWinActive || isHoldAndWinCompletionReload || isHoldAndWinMidFeatureReload || isHoldAndWinIntroReload);

      // First spin scenario: Wait for animations to complete
      const canInitialize =
        isReloadScenario ||
        (counterAnimationStarted &&
          !criticalAnimationPlaying &&
          (!isAnimatedWin || !winAnimationsPlaying));

      if (canInitialize && reloadWinValue > 0) {
        setDisplayedTotalWin(reloadWinValue);
        initializedRef.current = true;
      }
    }

    // Reset initialization flag only when status is fully null (cleared after outro dismissed).
    // CRITICAL: Do NOT reset when status is "played" — the last round's win still needs to
    // update displayedTotalWin via condition1, which requires initializedRef to stay true.
    if (
      freeRoundsStatus === null &&
      !isFreeRoundsActive &&
      !completionPending &&
      !freeRoundsEnded &&
      initializedRef.current
    ) {
      initializedRef.current = false;
      hasSpunRef.current = false; // Reset for next free rounds session
    }
  }, [
    isFreeRoundsActive,
    freeRoundsStatus,
    freeRoundsTotalWin,
    completionPending,
    freeRoundsEnded,
    criticalAnimationPlaying,
    isAnimatedWin,
    winAnimationsPlaying,
    isSpinning,
    counterAnimationStarted,
    isFreeRoundsCompletionReload,
    isHoldAndWinActive,
    isHoldAndWinCompletionReload,
    isHoldAndWinMidFeatureReload,
    isHoldAndWinIntroReload,
    currentRound?.win,
  ]);

  // CRITICAL: Clear internal state when localStorage is cleared (status becomes null)
  // This happens when rgsPlay.pending clears free rounds after completion
  // Ensures new batch starts with clean state (no cached displayedTotalWin)
  useEffect(() => {
    if (freeRoundsStatus === null && displayedTotalWin > 0) {
      setDisplayedTotalWin(0);
      initializedRef.current = false;
      hasSpunRef.current = false;
    }
  }, [freeRoundsStatus, displayedTotalWin]);

  // Add trigger round win to displayed total when it's shown (H&W trigger during free rounds)
  // CRITICAL: Only accumulate when hasSpunRef is true (live spin, not a reload replay).
  // On intro reload, the init effect already restored displayedTotalWin from storage —
  // adding again when counterAnimationStarted fires during the intro replay would double-count.
  useEffect(() => {
    if (
      isHoldAndWinTriggerRound &&
      counterAnimationStarted &&
      currentRound?.win > 0 &&
      initializedRef.current &&
      hasSpunRef.current
    ) {
      const triggerWin = currentRound.win;
      setDisplayedTotalWin(prev => prev + triggerWin);
    }
  }, [isHoldAndWinTriggerRound, counterAnimationStarted, currentRound?.win]);

  // True when H&W has a win in free rounds context.
  // Does NOT check !isHoldAndWinActive - the outro counter fires while holdAndWinActive
  // is still true (scene transition hasn't run yet), same as WinDisplay.hasHoldAndWinWin.
  const hasHoldAndWinWin =
    holdAndWinCurrentRound?.win > 0 &&
    (isFreeRoundsActive || freeRoundsStatus === "played" || completionPending || freeRoundsEnded);

  // On H&W completion reload during free rounds: restore holdAndWinOutroCounterFinished
  // so showCurrentWin can evaluate correctly and the "Win:" sub-line appears on mount.
  // Mirrors the identical pattern in WinDisplay.
  useEffect(() => {
    if (isHoldAndWinCompletionReload && hasHoldAndWinWin) {
      dispatch(setHoldAndWinOutroCounterFinished(true));
    }
  // Only run once when the component first sees these values on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHoldAndWinCompletionReload, hasHoldAndWinWin]);

  // Fallback path: requires H&W to be fully inactive AND scene transition done.
  // Used for the regular display path after the exit transition completes.
  const hasHoldAndWinWinAfterTransition =
    hasHoldAndWinWin && !isHoldAndWinActive && !sceneTransitionActive;

  // Update displayed total win when:
  // 1. Regular win animation starts (counterAnimationStarted) AND NOT in H&W AND critical animations finished AND Big Win finished (if applicable), OR
  // 2. Hold and Win completes (hasHoldAndWinWin becomes true) AND critical animations finished AND Big Win finished (if applicable), OR
  // 3. Free rounds completion reload (need to show total win immediately) - ONLY when already initialized, OR
  // 4. Win animation counter finishes (for Big Win, Mega Win, etc.) - update immediately, same timing as current win display, OR
  // 5. Hold and Win outro counter finishes - update total win immediately, same timing as current win display
  // CRITICAL: Must wait for criticalAnimationPlaying to finish (expanding wilds, etc.) EXCEPT for condition4 and condition5
  // CRITICAL: Must wait for winAnimationsPlaying to finish (Big Win popup, if applicable) EXCEPT for condition4 and condition5
  // CRITICAL: Reload bypass only works if component was already initialized (prevents early display on trigger rounds)
  // CRITICAL: Total win should ALWAYS update at same time as current win (when counterAnimationStarted OR winAnimationCounterFinished OR holdAndWinOutroCounterFinished)
  // CRITICAL: This effect is animation-driven, NOT data-driven (no freeRoundsTotalWin in deps)
  // CRITICAL: Condition1 blocked during H&W - trigger round win shows, then hides when H&W starts, then H&W win shows via condition2
  // CRITICAL: Condition2 does NOT require counterAnimationStarted - H&W timing controlled by sceneTransitionActive
  // CRITICAL: Condition4 and Condition5 update IMMEDIATELY when their respective counters finish (no animation waits)
  // This ensures total win updates in sync with the "Win: X" display AFTER all animations
  useEffect(() => {
    // Regular win counter or Big Win counter finished (no H&W active, animations done)
    // CRITICAL: Only fire when already initialized - first win is handled by the initialization effect above
    // CRITICAL: Only fire when freeRoundsTotalWin > 0 - prevents overwriting a fallback value
    //           (e.g. currentRound.win used as initial total win on H&W intro reload) with 0
    // CRITICAL: Do NOT update on H&W intro reload - displayedTotalWin already correctly restored by init effect
    //           The server's freeRoundsTotalWin doesn't include the trigger round win yet
    // CRITICAL: Skip ALL updates on H&W intro reload - displayedTotalWin already correctly initialized from localStorage
    // The server's freeRoundsTotalWin doesn't include the trigger round win yet, so any update would be wrong
    if (isHoldAndWinIntroReload) {
      return;
    }

    const condition1 = initializedRef.current && freeRoundsTotalWin > 0 && (counterAnimationStarted || winAnimationCounterFinished) && !isHoldAndWinActive && !criticalAnimationPlaying && (!isAnimatedWin || !winAnimationsPlaying);
    // H&W win fallback: after scene transition fully exits AND outro counter finished
    // CRITICAL: Must require holdAndWinOutroCounterFinished to prevent updating before counter plays
    const condition2 = hasHoldAndWinWinAfterTransition && holdAndWinOutroCounterFinished && !criticalAnimationPlaying && (!isAnimatedWin || !winAnimationsPlaying);
    // Big Win counter finished immediately
    const condition4 = winAnimationCounterFinished && !isHoldAndWinActive;
    // H&W outro counter finished - update immediately, no scene transition guard
    const condition5 = holdAndWinOutroCounterFinished && hasHoldAndWinWin;
    
    const shouldUpdate = condition1 || condition2 || condition4 || condition5;

    if (shouldUpdate) {
      setDisplayedTotalWin(freeRoundsTotalWin);
      if (!initializedRef.current) {
        initializedRef.current = true;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    counterAnimationStarted,
    winAnimationCounterFinished,
    holdAndWinOutroCounterFinished,
    isHoldAndWinActive,
    hasHoldAndWinWin,
    hasHoldAndWinWinAfterTransition,
    isFreeRoundsCompletionReload,
    criticalAnimationPlaying,
    isAnimatedWin,
    winAnimationsPlaying,
    freeRoundsTotalWin,
  ]);

  // Determine which win to display in "Win:" text.
  // Use H&W win once outro counter finished (even if H&W still active on completion reload),
  // otherwise use regular currentRound.win.
  const winToShow = (hasHoldAndWinWin && (holdAndWinOutroCounterFinished || !isHoldAndWinActive))
    ? holdAndWinCurrentRound.win
    : currentRound?.win || 0;

  // Current Win visible when:
  // 1. Regular win: counter started AND not in H&W AND animations done AND no scene transition
  // 2. H&W outro counter finished: show immediately, NO scene transition guard (same as WinDisplay)
  // 3. H&W win fallback: after scene transition fully exits
  // 4. Win animation counter finished (Big/Mega/SuperMega): show immediately
  // CRITICAL: !isHoldAndWinActive top-level guard hides win while H&W respins are running
  // CRITICAL: !sceneTransitionActive is NOT applied to the outro counter path (condition 2)
  //           so the win appears the moment the counter finishes, even mid-transition
  const showCurrentWin =
    // Regular win: must not be in H&W active
    (!isHoldAndWinActive &&
      winToShow > 0 &&
      !sceneTransitionActive &&
      !isSpinning &&
      counterAnimationStarted &&
      !criticalAnimationPlaying &&
      (!isAnimatedWin || !winAnimationsPlaying)) ||
    // H&W trigger round with Big Win: keep win visible after counter finishes until H&W takes over
    // Bridges the gap between Big Win closing and holdAndWinActive becoming true
    (wasHoldAndWinTriggerRoundRef.current && !isHoldAndWinActive && !isSpinning) ||
    // H&W outro counter finished: show immediately even while holdAndWinActive is still true
    // (same as WinDisplay - no !isHoldAndWinActive guard here)
    (hasHoldAndWinWin &&
      holdAndWinOutroCounterFinished &&
      !isSpinning) ||
    // H&W win fallback: after scene transition fully exits AND outro counter finished
    (hasHoldAndWinWinAfterTransition &&
      holdAndWinOutroCounterFinished &&
      !isSpinning &&
      !criticalAnimationPlaying &&
      (!isAnimatedWin || !winAnimationsPlaying)) ||
    // Big Win counter finished: must not be in H&W active
    (!isHoldAndWinActive &&
      winAnimationCounterFinished &&
      !isSpinning &&
      !criticalAnimationPlaying);

  // Animate Total Win position when Current Win appears/disappears
  useEffect(() => {
    if (!totalWinContainerRef.current) return;

    // Cancel any ongoing animation
    if (animationRef.current) {
      animationRef.current.pause();
      animationRef.current = null;
    }

    // On first win (displayedTotalWin is 0), set position immediately without animation
    // This prevents weird animation when component first appears
    if (displayedTotalWin === 0 && showCurrentWin) {
      totalWinContainerRef.current.y = -lineSpacing;
      return;
    }

    // If Total Win is already visible (displayedTotalWin > 0), animate normally
    // Animate UP when win is showing
    // Use showCurrentWin to keep animation in sync with visibility
    if (showCurrentWin) {
      // Win appears → Animate Total Win UP to top position
      animationRef.current = anime({
        targets: totalWinContainerRef.current,
        y: -lineSpacing,
        duration: 250,
        easing: "easeOutCubic",
        // Add update callback to check if target still exists
        update: (anim) => {
          if (!totalWinContainerRef.current) {
            anim.pause();
          }
        },
      });
    } else {
      // Win disappears → Animate Total Win DOWN to center
      animationRef.current = anime({
        targets: totalWinContainerRef.current,
        y: 0,
        duration: 150,
        easing: "easeInOutQuad",
        // Add update callback to check if target still exists
        update: (anim) => {
          if (!totalWinContainerRef.current) {
            anim.pause();
          }
        },
      });
    }

    return () => {
      // CRITICAL: Properly cleanup animation to prevent accessing null refs
      if (animationRef.current) {
        animationRef.current.pause();
        animationRef.current = null;
      }
    };
  }, [showCurrentWin, lineSpacing, displayedTotalWin]);

  // Dedicated unmount cleanup for anime.js animation
  // Ensures animation is cleaned up on component unmount regardless of which effect was last active
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        animationRef.current.pause();
        animationRef.current = null;
      }
    };
  }, []);

  // Format display values - must be before useMemo hooks
  const winText = formatToCustomLocale(winToShow);
  const totalWinText = formatToCustomLocale(displayedTotalWin);
  const currencyText = ` ${currency}`.toUpperCase();
  const winLabelText = "Win: ";
  const totalWinLabelText = "Total Win: ";

  // Memoize TextStyle objects to avoid recreating on every render
  const textStyles = useMemo(() => {
    return {
      winLabelStyle: new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "400",
        fill: "#ffffff",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
      winStyle: new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
        fill: "#ffbf11",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
      currencyStyle: new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
        fill: "#ffbf11",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    };
  }, [FONT_SIZE, LABEL_FONT_SIZE]);

  const { winLabelStyle, winStyle, currencyStyle } = textStyles;

  // Memoize text measurements to avoid expensive calculations on every render
  const textMetrics = useMemo(() => {
    const winLabelMetrics = PIXI.TextMetrics.measureText(
      winLabelText,
      winLabelStyle,
    );
    const winTextMetrics = PIXI.TextMetrics.measureText(winText, winStyle);
    const currencyTextMetrics = PIXI.TextMetrics.measureText(
      currencyText,
      currencyStyle,
    );
    const totalWinLabelMetrics = PIXI.TextMetrics.measureText(
      totalWinLabelText,
      winLabelStyle,
    );
    const totalWinTextMetrics = PIXI.TextMetrics.measureText(
      totalWinText,
      winStyle,
    );

    return {
      winLabelMetrics,
      winTextMetrics,
      currencyTextMetrics,
      totalWinLabelMetrics,
      totalWinTextMetrics,
      currentWinTextWidth:
        winLabelMetrics.width +
        winTextMetrics.width +
        currencyTextMetrics.width,
      totalWinTextWidth:
        totalWinLabelMetrics.width +
        totalWinTextMetrics.width +
        currencyTextMetrics.width,
    };
  }, [
    winText,
    totalWinText,
    currencyText,
    winLabelStyle,
    winStyle,
    currencyStyle,
  ]);

  const {
    winLabelMetrics,
    winTextMetrics,
    currencyTextMetrics,
    totalWinLabelMetrics,
    totalWinTextMetrics,
    currentWinTextWidth,
    totalWinTextWidth,
  } = textMetrics;

  // Visibility logic: Stay visible as long as free rounds state exists
  // CRITICAL: Check status instead of freeRoundsEnded flag
  // This ensures display persists after popups close until next base game spin clears the state

  // Show when:
  // 1. Free rounds are active (status === "active"), OR
  // 2. Free rounds just completed (status === "played")
  // Hide when status becomes null (cleared by next base game spin in rgsPlay.pending)
  const shouldShowBasedOnStatus =
    freeRoundsStatus === "active" || freeRoundsStatus === "played";

  if (!shouldShowBasedOnStatus) {
    return null;
  }

  // Hide until first free rounds win occurs
  // CRITICAL: Only show when we have accumulated free rounds wins (displayedTotalWin > 0)
  // This prevents showing base game wins that happened before free rounds started
  // (e.g., when heartbeat triggers free rounds while base game win animations are playing)
  const hasAnyWin = displayedTotalWin > 0;

  if (!hasAnyWin) {
    return null;
  }

  return (
    <Container
      name="free_rounds_win_display"
      x={winConfig.x}
      y={winConfig.y}
      zIndex={4}
    >
      {/* Total Win Line (always visible during free rounds, animated position) */}
      <Container ref={totalWinContainerRef} name="total_win_line" y={0}>
        {/* Total win label */}
        <Text
          key="total_win_label"
          text={totalWinLabelText}
          style={winLabelStyle}
          anchor={0.5}
          x={Math.round(
            -totalWinTextWidth / 2 + totalWinLabelMetrics.width / 2,
          )}
          resolution={2}
        />
        {/* Total win amount */}
        <Text
          key="total_win_amount"
          text={totalWinText}
          style={winStyle}
          anchor={0.5}
          x={Math.round(
            -totalWinTextWidth / 2 +
              totalWinLabelMetrics.width +
              totalWinTextMetrics.width / 2,
          )}
          resolution={2}
        />
        {/* Currency */}
        <Text
          key="total_win_currency"
          text={currencyText}
          style={currencyStyle}
          anchor={0.5}
          x={Math.round(totalWinTextWidth / 2 - currencyTextMetrics.width / 2)}
          resolution={2}
        />
      </Container>

      {/* Current Win Line (shown temporarily after each spin) */}
      {showCurrentWin && (
        <Container name="current_win_line" y={lineSpacing}>
          {/* Win label text (white, static) */}
          <Text
            key={winLabelText}
            text={winLabelText}
            style={winLabelStyle}
            anchor={0.5}
            x={Math.round(-currentWinTextWidth / 2 + winLabelMetrics.width / 2)}
            resolution={2}
          />
          {/* Win amount text (golden, animated counter) */}
          <Text
            key={winText}
            text={winText}
            style={winStyle}
            anchor={0.5}
            x={Math.round(
              -currentWinTextWidth / 2 +
                winLabelMetrics.width +
                winTextMetrics.width / 2,
            )}
            resolution={2}
          />
          {/* Currency text (golden, static) */}
          <Text
            key={currencyText}
            text={currencyText}
            style={currencyStyle}
            anchor={0.5}
            x={Math.round(
              currentWinTextWidth / 2 - currencyTextMetrics.width / 2,
            )}
            resolution={2}
          />
        </Container>
      )}
    </Container>
  );
};

export default FreeRoundsWinDisplay;
